// A host's side of the headless chat (ADR 0021 amendment of 2026-10-08) for the app it frames: cortex's Apps
// page, a shared app, the prototype preview and a project's standalone shell mount it beside their iframe.
// The app's own chat UI (`@cortex/app-sdk/chat`) asks by postMessage; this answers through the chat SDK's own
// routes — the person's session, the app named on every call (`x-cortex-app`) — as the Data analyst.
import { appChatScopeOf, serveAppChat, type AppChatScope } from '@cortex/app-sdk/chat/host';
import { vizHost } from '@cortex/viz/browser';
import * as React from 'react';

/** Draws a model-authored visualization to sanitized SVG markup in this page's viz sandbox (ADR 0013). */
export async function renderVizSvg(p: {
  code: string;
  data: unknown;
  width: number;
  height?: number;
}): Promise<string> {
  const out = await vizHost().render({
    code: p.code,
    data: p.data,
    width: p.width,
    ...(p.height ? { height: p.height } : {}),
  });
  if (!out.ok) throw new Error(out.message);
  return out.markup;
}

export interface AppChatBridgeHost {
  /** The api the chat SDK's routes are on (this origin, or the host's api base). */
  apiBaseUrl: string;
  assistantBaseUrl: string;
  getToken: () => string | undefined | null;
  /** The companions route's answer for the app (named by it), or null. */
  companions: (
    slug: string,
    app: string,
  ) => Promise<{ companions?: { key: string }[]; chatUi?: string; reach?: string; title?: string } | null>;
  /** The path of a saved chat report's page on this host. */
  reportPath: (slug: string, id: string) => string;
  /** What the host's turn route takes beside the chat's own fields (the host's own; none: the chat's alone). */
  turnFields?: Readonly<Record<string, string>>;
}

/**
 * Serves the headless chat to the app in `frame` while it is open (`runtimeUrl` is where the frame's page
 * comes from: only that origin is heard). No app or no runtime: nothing is served.
 */
export function useAppChatBridge(
  frame: React.RefObject<HTMLIFrameElement | null>,
  p: { slug: string; app: string | null; runtimeUrl: string | null; host: AppChatBridgeHost },
) {
  const { slug, app, runtimeUrl, host } = p;
  React.useEffect(() => {
    if (!app || !runtimeUrl) return;
    let origin: string;
    try {
      origin = new URL(runtimeUrl, window.location.href).origin;
    } catch {
      return;
    }
    return serveAppChat({
      frame: () => frame.current?.contentWindow ?? null,
      origin,
      slug,
      ...(host.turnFields ? { turnFields: host.turnFields } : {}),
      app,
      apiBaseUrl: new URL(host.apiBaseUrl, window.location.origin).toString(),
      assistantBaseUrl: new URL(host.assistantBaseUrl, window.location.origin).toString(),
      getToken: host.getToken,
      scope: async (): Promise<AppChatScope> => appChatScopeOf(await host.companions(slug, app)),
      renderVizSvg,
      reportUrl: (id) => new URL(host.reportPath(slug, id), window.location.origin).toString(),
    });
    // The host's readers are stable (a module constant or a memo); the app and its frame's origin are what change.
  }, [slug, app, runtimeUrl, host]);
}
